import { useEffect, useMemo, useState } from "react";
import { clsx } from "clsx";
import { Download, Paperclip, Pencil, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { CommentBody } from "./CommentBody";
import {
  useCanSeeProcessGraph,
  useGraphNodePhases,
  useProcessGraph,
} from "../advisor/hooks";
import type { GraphNode } from "../advisor/types";
import {
  useConnectionFileMutations,
  useConnectionFiles,
  useMilestoneNoteMutations,
  useMilestoneNotes,
  useSetMilestone,
} from "../hooks";
import type { ConnectionFile, Milestone, MilestoneState, PowerProject } from "../types";
import { apiErr, fmtDate, fmtDateTime } from "../utils";
import { milestoneMeta, MILESTONE_STATES, MILESTONE_STATE_RANK } from "./ui";

const PHASE_MARK: Record<string, string> = {
  done: "●",
  active: "▶",
  pending: "○",
};

function fmtBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Wiersz pliku kamienia. Opis (komentarz) wpisany przy wgrywaniu da się tu dopisać
 * albo poprawić w miejscu (ołówek → pole → Zapisz/Enter) — wcześniej opis szło ustawić
 * WYŁĄCZNIE w chwili przesyłania, a potem tylko z zakładki „Pliki".
 */
function MilestoneFileRow({
  file: cf,
  slotLabel,
  saving,
  onSaveDescription,
  onDelete,
}: {
  file: ConnectionFile;
  slotLabel: string | null;
  saving: boolean;
  onSaveDescription: (description: string, close: () => void) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(cf.description ?? "");
  const startEdit = () => {
    setDraft(cf.description ?? "");
    setEditing(true);
  };
  const save = () => onSaveDescription(draft.trim(), () => setEditing(false));

  return (
    <li className="flex items-center gap-2 px-3 py-2">
      <Paperclip size={13} className="shrink-0 text-[var(--color-text-muted)]" />
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-sm text-[var(--color-text-primary)]"
          title={cf.file.original_filename}
        >
          {cf.file.original_filename}
        </p>
        {editing ? (
          <div className="mt-1 flex items-center gap-1.5">
            <input
              className="input-base text-xs py-1 w-full"
              value={draft}
              autoFocus
              placeholder="Komentarz do pliku"
              aria-label="Komentarz do pliku"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <Button size="sm" loading={saving} onClick={save}>
              Zapisz
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Anuluj
            </Button>
          </div>
        ) : (
          <p className="flex min-w-0 items-center gap-1.5 font-mono text-[10px] text-[var(--color-text-muted)]">
            <span className="truncate">
              {slotLabel ? `${slotLabel} · ` : ""}
              {cf.description ? `${cf.description} · ` : ""}
              {fmtBytes(cf.size_bytes)} · {fmtDateTime(cf.created_at)}
            </span>
            <button
              type="button"
              className="shrink-0 p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title={cf.description ? "Edytuj komentarz" : "Dodaj komentarz"}
              aria-label={cf.description ? "Edytuj komentarz" : "Dodaj komentarz"}
              onClick={startEdit}
            >
              <Pencil size={11} />
            </button>
          </p>
        )}
      </div>
      <a
        href={cf.file.url ?? "#"}
        download={cf.file.original_filename}
        target="_blank"
        rel="noreferrer"
        className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        title="Pobierz"
      >
        <Download size={15} />
      </a>
      <button
        type="button"
        className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        title="Usuń plik"
        onClick={onDelete}
      >
        <Trash2 size={15} />
      </button>
    </li>
  );
}

/**
 * JEDEN modal szczegółów kroku — otwierany zarówno z tablicy kamieni (siatka /
 * kanban), jak i kliknięciem kafelka na grafie procesu (schemat blokowy). Wcześniej
 * każdy z tych widoków miał własny modal: tablica pokazywała notatki i pliki bez faz
 * i historii, graf fazy i historię bez notatek i plików — ten sam kamień czytał się
 * inaczej w zależności od tego, skąd się w niego weszło.
 *
 * Dane kroku: kamień z tablicy (status, terminy, notatki, pliki) + węzeł grafu
 * (fazy, prognoza, ścieżka krytyczna, niedomknięte poprzedniki, historia). Węzeł
 * dociągamy tylko osobom z dostępem do grafu — dla reszty te sekcje po prostu nie
 * ma skąd wziąć, reszta modala działa tak samo.
 */
export function MilestoneDetailModal({
  connection,
  milestone,
  initialState,
  onClose,
}: {
  connection: PowerProject;
  milestone: Milestone;
  /** Status wybrany wcześniej na karcie / przeciągnięciem, który wymaga powodu —
   *  modal otwiera się od razu z prośbą o powód, zamiast kazać klikać drugi raz. */
  initialState?: MilestoneState;
  onClose: () => void;
}) {
  const canSeeGraph = useCanSeeProcessGraph();
  const graphQ = useProcessGraph(canSeeGraph ? connection.id : undefined);
  const node: GraphNode | undefined = graphQ.data?.nodes.find(
    (n) => n.key === milestone.key,
  );

  const setMilestone = useSetMilestone(connection.id);
  const phasesM = useGraphNodePhases(connection.id);
  const notes = useMilestoneNotes(connection.id, milestone.key);
  const noteM = useMilestoneNoteMutations(connection.id, milestone.key);
  const filesQ = useConnectionFiles(connection.id);
  const fileM = useConnectionFileMutations(connection.id);

  // Stan wybrany, ale jeszcze niezapisany — czeka na powód (cofnięcie / zamknięcie
  // poza kolejnością). Backend pilnuje tego samego, więc bez powodu i tak odmówi.
  const [pendingState, setPendingState] = useState<MilestoneState | null>(
    initialState ?? null,
  );
  const [reason, setReason] = useState("");
  const [stateError, setStateError] = useState<string | null>(null);
  const [dueDraft, setDueDraft] = useState(milestone.due_date ?? "");
  // Data FAKTYCZNEGO zakończenia etapu. Projekty trafiają do systemu wstecz (umowa z
  // marca, projekt zakładany w lipcu), więc maszynowy stempel „dziś" wpisywał do raportu
  // dla operatora dzień kliknięcia, a nie dzień, w którym etap naprawdę się zamknął.
  const [doneDraft, setDoneDraft] = useState(milestone.completed_at ?? "");
  const [doneError, setDoneError] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  // @oznaczenia w notatce — oznaczony dostaje powiadomienie z linkiem do tego kamienia.
  const [noteMentions, setNoteMentions] = useState<string[]>([]);
  const { data: internalUsers = [] } = useInternalUsers();
  const mentionSuggestions = useMemo(
    () => internalUsers.map((u) => ({ id: u.id, name: u.full_name })),
    [internalUsers],
  );
  const [files, setFiles] = useState<File[]>([]);
  const [fileDesc, setFileDesc] = useState("");
  // Krok, który wydaje dwa rodzaje artefaktu (MDCP + FOTO), dostaje z backendu listę pól
  // wgrywania — wybór decyduje o folderze docelowym (mapa → MAPY / Surowe, zdjęcia →
  // INWENTARYZACJA TERENU).
  const fileSlots = milestone.file_slots ?? [];
  const [slot, setSlot] = useState(fileSlots[0]?.key ?? "");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [editPhases, setEditPhases] = useState(false);

  const submitPhase = (node?.phases ?? []).find((p) => p.name === "submit");
  const resolvePhase = (node?.phases ?? []).find((p) => p.name === "resolve");
  const [pf, setPf] = useState<{
    submit_label: string;
    resolve_label: string;
    submit_sla_days: number | null;
    resolve_sla_days: number | null;
  }>({
    submit_label: "",
    resolve_label: "",
    submit_sla_days: null,
    resolve_sla_days: null,
  });
  // Graf dociąga się po otwarciu modala, więc formularz faz wypełniamy dopiero, gdy
  // węzeł dojdzie — i nie nadpisujemy tego, co użytkownik właśnie wpisuje.
  useEffect(() => {
    if (editPhases) return;
    setPf({
      submit_label: submitPhase?.label ?? "",
      resolve_label: resolvePhase?.label ?? "",
      submit_sla_days: submitPhase?.sla_days ?? null,
      resolve_sla_days: resolvePhase?.sla_days ?? null,
    });
  }, [
    editPhases,
    submitPhase?.label,
    submitPhase?.sla_days,
    resolvePhase?.label,
    resolvePhase?.sla_days,
  ]);

  useEffect(() => {
    setDoneDraft(milestone.completed_at ?? "");
    setDoneError(null);
  }, [milestone.completed_at]);

  // Data zakończenia ma sens tylko dla kroku zamkniętego — backend pilnuje tego samego.
  const isDone = milestone.state === "done";
  const todayIso = useMemo(() => {
    const d = new Date();
    const p2 = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  }, []);

  const meta = milestoneMeta(milestone.state);
  const missingPrereqs = node?.missing_prereqs ?? [];
  // Historia = faktyczne przejścia. Zapis samego terminu też zostawia wpis (serwis
  // loguje każdy `set_state`), ale „Gotowe → Gotowe" nic nie mówi, więc go nie pokazujemy.
  const history = (node?.history ?? []).filter((h) => h.from !== h.to);

  // Powód wymagany: cofnięcie stanu ALBO domknięcie kroku z niedomkniętymi
  // poprzednikami. Lustro backendowego MilestoneService.guard_manual_state_change.
  const isRevert = (s: MilestoneState) =>
    MILESTONE_STATE_RANK[s] < MILESTONE_STATE_RANK[milestone.state];
  const isOutOfOrderClose = (s: MilestoneState) =>
    s === "done" &&
    MILESTONE_STATE_RANK[s] > MILESTONE_STATE_RANK[milestone.state] &&
    missingPrereqs.length > 0;
  const needsReason = (s: MilestoneState) =>
    isRevert(s) || isOutOfOrderClose(s);

  const applyState = (s: MilestoneState, why: string) => {
    setStateError(null);
    setMilestone.mutate(
      { mkey: milestone.key, state: s, detail: why.trim() || undefined },
      {
        onSuccess: () => {
          setPendingState(null);
          setReason("");
        },
        // Backend odmówił (najczęściej: brak powodu przy cofnięciu) — pokaż jego
        // komunikat i poproś o powód zamiast cicho zostawić stary status.
        onError: (err) => {
          setPendingState(s);
          setStateError(apiErr(err, "Nie udało się zmienić statusu."));
        },
      },
    );
  };

  const pickState = (s: MilestoneState) => {
    if (s === milestone.state) return;
    setStateError(null);
    if (needsReason(s)) {
      setPendingState(s);
      setReason("");
      return;
    }
    applyState(s, "");
  };

  const saveDue = () => {
    setMilestone.mutate({ mkey: milestone.key, due_date: dueDraft || null });
  };

  const saveCompleted = () => {
    setDoneError(null);
    setMilestone.mutate(
      { mkey: milestone.key, completed_at: doneDraft || null },
      {
        onError: (err) =>
          setDoneError(apiErr(err, "Nie udało się zapisać daty zakończenia.")),
      },
    );
  };

  const addNote = () => {
    const text = noteDraft.trim();
    if (!text) return;
    noteM.add.mutate(
      { text, mentionIds: noteMentions },
      { onSuccess: () => { setNoteDraft(""); setNoteMentions([]); } },
    );
  };

  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of files) {
        await fileM.upload.mutateAsync({
          file,
          description: fileDesc.trim(),
          milestone: milestone.id,
          milestoneSlot: slot || undefined,
        });
      }
      setFiles([]);
      setFileDesc("");
    } catch {
      setUploadError("Nie udało się przesłać niektórych plików.");
    } finally {
      setUploading(false);
    }
  };

  // Files tagged with this milestone — a subset of the connection's files (which the
  // Pliki tab lists in full). Filtered client-side off the shared cache.
  const milestoneFiles = (filesQ.data ?? []).filter(
    (f) => f.milestone === milestone.id,
  );

  const phaseStatusText = (p: NonNullable<GraphNode["phases"]>[number]) => {
    if (p.status === "done")
      return p.done_at ? `zrobione ${fmtDate(p.done_at)}` : "zrobione";
    if (p.status === "active")
      return p.since ? `w toku od ${fmtDate(p.since)}` : "w toku";
    return "czeka";
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Kamień milowy — ${milestone.type_display}`}
      size="lg"
    >
      <div className="space-y-6">
        {/* ── status + zmiana statusu ───────────────────────────────────── */}
        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={meta.color}>{meta.label}</Badge>
              {node?.state_since && milestone.state !== "not_started" && (
                <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                  od {fmtDate(node.state_since)}
                </span>
              )}
              {node?.is_critical && (
                <Badge color="orange">◆ ścieżka krytyczna</Badge>
              )}
              {node?.sla_days ? (
                <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                  SLA {node.sla_days} dni
                </span>
              ) : null}
            </div>
            {milestone.care_arrangement ? (
              <span
                className="font-mono text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 border border-amber-500 px-1 leading-tight"
                title="Kamień dodany automatycznie z ustaleń karty specyfiki oddziału klienta"
              >
                z ustaleń: {milestone.care_source ?? "oddział klienta"}
              </span>
            ) : milestone.is_custom && (
              <span className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)] border border-[var(--color-border)] px-1 leading-tight">
                kamień własny
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {MILESTONE_STATES.map((s) => {
              const sm = milestoneMeta(s);
              const current = s === milestone.state;
              const picked = s === pendingState;
              return (
                <button
                  key={s}
                  type="button"
                  disabled={setMilestone.isPending}
                  onClick={() => pickState(s)}
                  className={clsx(
                    "px-1.5 py-1 border transition-colors disabled:opacity-50",
                    current
                      ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                      : picked
                        ? "border-dashed border-[#b45309] bg-[var(--color-bg-elevated)]"
                        : "border-transparent hover:border-[var(--color-border-strong)]",
                  )}
                  title={
                    current
                      ? `Bieżący status — ${sm.hint}`
                      : `Ustaw status: ${sm.label} (${sm.hint})`
                  }
                >
                  <Badge color={sm.color}>{sm.label}</Badge>
                </button>
              );
            })}
          </div>

          {/* powód wymagany — cofnięcie albo domknięcie mimo niedomkniętych poprzedników */}
          {pendingState && (
            <div
              className="space-y-2 border border-dashed p-3"
              style={{ borderColor: "#b45309" }}
            >
              <span className="section-label" style={{ color: "#b45309" }}>
                {isRevert(pendingState)
                  ? `Cofnięcie na „${milestoneMeta(pendingState).label}" — powód (wymagany)`
                  : missingPrereqs.length
                    ? `⚠ Zamknięcie poza kolejnością — niedomknięte: ${missingPrereqs.join(", ")}. Powód (wymagany)`
                    : "⚠ Zamknięcie kroku poza kolejnością — powód (wymagany)"}
              </span>
              <input
                autoFocus
                className="input-base w-full"
                value={reason}
                placeholder={
                  isRevert(pendingState)
                    ? "np. zmiana trasy — ZUD nieaktualny"
                    : "np. zrobione poza systemem / krok nie blokuje"
                }
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && reason.trim())
                    applyState(pendingState, reason);
                }}
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  disabled={!reason.trim()}
                  loading={setMilestone.isPending}
                  onClick={() => applyState(pendingState, reason)}
                >
                  {isRevert(pendingState) ? "↩ Cofnij status" : "Zapisz status"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setPendingState(null);
                    setReason("");
                    setStateError(null);
                  }}
                >
                  Anuluj
                </Button>
              </div>
            </div>
          )}
          {stateError && (
            <p className="font-mono text-xs text-[var(--color-primary)]">
              {stateError}
            </p>
          )}
          {node?.revert_note && (
            <p className="text-xs" style={{ color: "#b45309" }}>
              ↩ Ostatnio cofnięty: {node.revert_note}
            </p>
          )}
          {node?.violation && missingPrereqs.length > 0 && (
            <p className="text-xs text-red-700">
              ⚠ Krok jest w toku, ale wymaga wcześniej:{" "}
              <b>{missingPrereqs.join(", ")}</b>.
            </p>
          )}
        </section>

        {/* ── terminy: umówiony termin, ukończenie, prognoza z grafu ─────── */}
        <section className="space-y-2">
          <span className="section-label">Terminy</span>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="section-label">Termin</span>
              <div className="flex items-center gap-1.5">
                <DatePicker
                  value={dueDraft}
                  onChange={setDueDraft}
                  aria-label="Termin"
                />
                {(dueDraft || "") !== (milestone.due_date ?? "") && (
                  <Button
                    size="sm"
                    loading={setMilestone.isPending}
                    onClick={saveDue}
                  >
                    Zapisz
                  </Button>
                )}
              </div>
            </label>
            {isDone && (
              <label className="flex flex-col gap-1">
                <span className="section-label">Data zakończenia</span>
                <div className="flex items-center gap-1.5">
                  <DatePicker
                    value={doneDraft}
                    onChange={setDoneDraft}
                    max={todayIso}
                    aria-label="Data zakończenia"
                  />
                  {(doneDraft || "") !== (milestone.completed_at ?? "") && (
                    <Button
                      size="sm"
                      loading={setMilestone.isPending}
                      onClick={saveCompleted}
                    >
                      Zapisz
                    </Button>
                  )}
                </div>
                <span className="max-w-[22rem] text-[11px] text-[var(--color-text-muted)]">
                  Data FAKTYCZNEGO zakończenia etapu — to ona trafia do raportu z postępu
                  prac dla operatora. Nie wpisuj daty kliknięcia „gotowe”.
                </span>
              </label>
            )}
            <div className="flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] text-[var(--color-text-muted)]">
              {!isDone && milestone.completed_at && (
                <span>ukończono: {fmtDate(milestone.completed_at)}</span>
              )}
              {node?.es && <span>prognoza startu: {fmtDate(node.es)}</span>}
              {node?.ef && <span>prognoza końca: {fmtDate(node.ef)}</span>}
            </div>
          </div>
          {doneError && (
            <p className="font-mono text-xs text-[var(--color-primary)]">{doneError}</p>
          )}
          {node?.order_ref && (
            <p className="font-mono text-[11px] text-[var(--color-text-muted)]">
              Zamówienie {node.order_ref.number} — termin{" "}
              {fmtDate(node.order_ref.deadline)}
            </p>
          )}
        </section>

        {/* ── fazy kroku (z grafu procesu) ───────────────────────────────── */}
        {(node?.phases ?? []).length > 0 && (
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="section-label">Fazy kroku</span>
              <button
                type="button"
                className="text-xs text-[var(--color-primary)]"
                onClick={() => setEditPhases((v) => !v)}
              >
                {editPhases
                  ? "schowaj edycję"
                  : "✎ dostosuj fazy (ten projekt)"}
              </button>
            </div>
            <div className="space-y-1.5">
              {node!.phases!.map((p) => (
                <div key={p.name} className="flex items-start gap-2">
                  <span
                    style={{
                      color:
                        p.status === "done"
                          ? "#16a34a"
                          : p.status === "active"
                            ? "var(--color-primary)"
                            : "var(--color-text-muted)",
                      fontWeight: 700,
                    }}
                  >
                    {PHASE_MARK[p.status]}
                  </span>
                  <div className="min-w-0">
                    <div
                      className={
                        p.status === "active"
                          ? "font-semibold"
                          : p.status === "pending"
                            ? "text-[var(--color-text-muted)]"
                            : ""
                      }
                    >
                      {p.label}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)]">
                      {p.side === "ours"
                        ? "po naszej stronie"
                        : "strona zewnętrzna"}
                      {p.sla_days ? ` · SLA ${p.sla_days} dni` : ""} ·{" "}
                      {phaseStatusText(p)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {editPhases && (
              <div className="mt-3 space-y-2 border border-dashed border-[var(--color-border)] p-3">
                <div className="font-mono text-[10px] text-[var(--color-text-muted)]">
                  Zmiany działają TYLKO w tym projekcie (globalnie / per klient
                  — zakładka „Szablony procesu" w nadzorze). „Resetuj do
                  szablonu" przywróci wartości z szablonu.
                </div>
                <div className="grid grid-cols-[1fr_5rem] items-end gap-2">
                  <label className="flex flex-col gap-1">
                    <span className="section-label">Faza 1 — nasza akcja</span>
                    <input
                      className="input-base"
                      value={pf.submit_label}
                      onChange={(e) =>
                        setPf({ ...pf, submit_label: e.target.value })
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="section-label">SLA [dni]</span>
                    <input
                      type="number"
                      className="input-base"
                      value={pf.submit_sla_days ?? ""}
                      onChange={(e) =>
                        setPf({
                          ...pf,
                          submit_sla_days:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        })
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="section-label">
                      Faza 2 — odpowiedź strony
                    </span>
                    <input
                      className="input-base"
                      value={pf.resolve_label}
                      onChange={(e) =>
                        setPf({ ...pf, resolve_label: e.target.value })
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="section-label">SLA [dni]</span>
                    <input
                      type="number"
                      className="input-base"
                      value={pf.resolve_sla_days ?? ""}
                      onChange={(e) =>
                        setPf({
                          ...pf,
                          resolve_sla_days:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        })
                      }
                    />
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    loading={phasesM.isPending}
                    onClick={() =>
                      phasesM.mutate(
                        { node: milestone.key, ...pf },
                        { onSuccess: () => setEditPhases(false) },
                      )
                    }
                  >
                    Zapisz fazy dla tego projektu
                  </Button>
                  {phasesM.isError && (
                    <span className="text-xs text-[var(--color-primary)]">
                      Nie zapisano.
                    </span>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── notatki (najnowsza na górze) ─────────────────────────────── */}
        <section className="space-y-3">
          <span className="section-label">Notatki</span>
          <div className="space-y-2">
            <RichTextEditor
              value={noteDraft}
              onChange={setNoteDraft}
              onMentionIdsChange={setNoteMentions}
              mentionSuggestions={mentionSuggestions}
              placeholder="Dodaj notatkę… (wpisz @ aby oznaczyć osobę)"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                @ — oznaczona osoba dostanie powiadomienie z linkiem do tego kamienia
              </span>
              <Button
                size="sm"
                disabled={!noteDraft.trim()}
                loading={noteM.add.isPending}
                onClick={addNote}
              >
                Dodaj notatkę
              </Button>
            </div>
          </div>

          {notes.isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : notes.data && notes.data.length > 0 ? (
            <ul className="space-y-2">
              {notes.data.map((n) => (
                <li
                  key={n.id}
                  className="group border-l-2 border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)] pl-3 pr-2 py-2"
                >
                  <CommentBody
                    content={n.text}
                    mentions={n.mentions ?? []}
                    className="text-sm text-[var(--color-text-primary)]"
                  />
                  <div className="mt-1 flex items-center gap-2 font-mono text-[10px] text-[var(--color-text-muted)]">
                    <span>{n.author_name ?? "System"}</span>
                    <span aria-hidden>·</span>
                    <span>{fmtDateTime(n.created_at)}</span>
                    <button
                      type="button"
                      onClick={() => noteM.remove.mutate(n.id)}
                      disabled={noteM.remove.isPending}
                      className="ml-auto opacity-0 group-hover:opacity-100 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-opacity disabled:opacity-50"
                      title="Usuń notatkę"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] italic">
              Brak notatek.
            </p>
          )}
        </section>

        {/* ── pliki ────────────────────────────────────────────────────── */}
        <section className="space-y-3">
          <span className="section-label">Pliki</span>

          <div className="space-y-2">
            {/* Krok wydający dwa rodzaje artefaktu (MDCP + FOTO): wybór pola decyduje,
                do którego folderu zakładki „Pliki" trafi wgranie. */}
            {fileSlots.length > 1 && (
              <div className="flex flex-wrap items-center gap-1.5">
                {fileSlots.map((s) => (
                  <button
                    key={s.key || "default"}
                    type="button"
                    onClick={() => setSlot(s.key)}
                    className={clsx(
                      "border px-2 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors",
                      slot === s.key
                        ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                        : "border-[var(--color-border-muted)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <FileDropZone multiple files={files} onFilesChange={setFiles} />
            {files.length > 0 && (
              <input
                className="input-base text-xs py-1 w-full"
                placeholder="Opis (opcjonalnie, wspólny dla przesyłanych plików)"
                value={fileDesc}
                onChange={(e) => setFileDesc(e.target.value)}
              />
            )}
            {uploadError && (
              <p className="text-xs text-[var(--color-primary)] font-mono">
                {uploadError}
              </p>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                Dołączone tu pliki pojawią się też w zakładce „Pliki".
                {milestone.key === "wypis" &&
                  " Wypisy PDF trafiają od razu na zakładkę 'Działki'."}
              </span>
              <Button
                size="sm"
                disabled={files.length === 0}
                loading={uploading}
                onClick={upload}
              >
                Prześlij {files.length > 1 ? `(${files.length})` : ""}
              </Button>
            </div>
          </div>

          {filesQ.isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : milestoneFiles.length > 0 ? (
            <ul className="divide-y divide-[var(--color-border-muted)] border border-[var(--color-border-muted)]">
              {milestoneFiles.map((cf) => (
                <MilestoneFileRow
                  key={cf.id}
                  file={cf}
                  slotLabel={
                    cf.milestone_slot
                      ? fileSlots.find((s) => s.key === cf.milestone_slot)?.label ?? cf.milestone_slot
                      : null
                  }
                  saving={fileM.update.isPending}
                  onSaveDescription={(description, close) =>
                    fileM.update.mutate({ id: cf.id, description }, { onSuccess: close })
                  }
                  onDelete={() => {
                    if (confirm(`Usunąć plik „${cf.file.original_filename}"?`))
                      fileM.remove.mutate(cf.id);
                  }}
                />
              ))}
            </ul>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)] italic">
              Brak plików dla tego kamienia.
            </p>
          )}
        </section>

        {/* ── historia zmian stanu (z grafu procesu) ─────────────────────── */}
        {history.length > 0 && (
          <section className="space-y-1">
            <span className="section-label">Historia zmian statusu</span>
            <div className="space-y-1">
              {history.map((h, i) => (
                <div key={i} className="flex flex-wrap gap-x-1 text-xs">
                  <span className="font-mono">{fmtDate(h.at)}</span>
                  <span>
                    {h.from
                      ? milestoneMeta(h.from as MilestoneState).label
                      : "—"}{" "}
                    → <b>{milestoneMeta(h.to as MilestoneState).label}</b>
                  </span>
                  {h.by && (
                    <span className="text-[var(--color-text-muted)]">
                      · {h.by}
                    </span>
                  )}
                  {h.detail && (
                    <span className="text-[var(--color-text-muted)]">
                      — {h.detail}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="flex justify-end border-t border-[var(--color-border-muted)] pt-3">
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X size={13} className="mr-1.5" />
            Zamknij
          </Button>
        </div>
      </div>
    </Modal>
  );
}
